import { IPlan } from "~/types/billing";
import { getTotalPlanCost } from "./numbers";
import { isDevelopment } from "./environment";
import { logger } from "./logger";

/**
 * Bing Ads UET (Universal Event Tracking) Event Functions
 *
 * These functions handle Bing Ads event tracking using the UET (Universal Event Tracking) API.
 * UET events are sent to the window.uetq array which is processed by the Bing Ads tracking script.
 */

/**
 * Generic function to trigger Bing Ads UET events
 * @param eventName - The name of the event to track
 * @param eventData - Additional data to send with the event
 */
export function triggerBingAdsEvent(
  eventName: string,
  eventData: Record<string, any> = {}
) {
  if (typeof window === "undefined") {
    return;
  }

  // Don't push events in development
  if (isDevelopment) {
    console.log("Development mode - skipping UET event:", eventName);
    return;
  }

  // @ts-ignore
  window.uetq = window.uetq || [];

  // @ts-ignore
  window.uetq.push("event", eventName, eventData);

  logger.trackEvent("bing_ads_event", { eventName, eventData });
}

/**
 * Trigger Bing Ads event when a customer starts a trial
 * @param plan - The plan that the customer is starting a trial for
 * @param account - Optional account data to get trial end date from active subscription
 */
export function triggerBingAdsTrialStart(plan: IPlan) {
  try {
    // Only send Bing Ads trial event if utm_source=bing is associated with the user
    if (typeof window !== "undefined") {
      const utmSource = localStorage.getItem("utm_source");
      const utmCampaign = localStorage.getItem("utm_campaign");
      const utmMedium = localStorage.getItem("utm_medium");

      if (!utmSource || utmSource.toLowerCase() !== "bing") {
        return;
      }

      // Add UTM parameters to the tracking event
      logger.setContext("utm", {
        source: utmSource,
        campaign: utmCampaign,
        medium: utmMedium,
      });
    }

    logger.trackEvent("triggerBingAdsTrialStartEventData:", {
      planName: plan.name,
      planInterval: plan.interval,
      revenueValue: getTotalPlanCost(plan),
      planId: plan.id,
    });

    triggerBingAdsEvent("start_trial", {
      event_category: "Subscription",
      event_action: "TrialStarted",
      event_label: `Trial_${plan.name}_${plan.interval}`,
      revenue_value: getTotalPlanCost(plan),
    });
  } catch (error) {
    logger.error("triggerBingAdsTrialStartErrorOccurred:", error);
  }
}
